import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { useLanguageStore } from "~/stores/language-store";

export type PastPractitionerOption = {
    id: number;
    title: string | null;
    firstName: string;
    lastName: string;
    type: 'DOCTOR' | 'COSMETICIAN' | 'MFA';
};

export const formatPractitionerName = (practitioner: PastPractitionerOption): string => {
    return [practitioner.title, practitioner.firstName, practitioner.lastName].filter(Boolean).join(' ');
};

interface PastPractitionerPickerProps {
    practitioners: PastPractitionerOption[];
    selectedPractitionerId: number | null;
    onChange: (practitioner: PastPractitionerOption | null) => void;
}

const PastPractitionerPicker = ({ practitioners, selectedPractitionerId, onChange }: PastPractitionerPickerProps) => {
    const { t } = useLanguageStore();
    const [open, setOpen] = useState(false);

    const selectedPractitioner = practitioners.find((practitioner) => practitioner.id === selectedPractitionerId);

    const handleSelectNoPreference = () => {
        if (!selectedPractitioner) {
            return;
        }
        onChange(null);
    };

    const handleSelect = (practitioner: PastPractitionerOption) => {
        if (practitioner.id === selectedPractitionerId) {
            return;
        }
        onChange(practitioner);
    };

    const ChevronIcon = open ? ChevronUp : ChevronDown;
    const itemClassName = "rounded-none px-4 py-2.5 text-[11px] uppercase leading-snug tracking-wide text-burgundy cursor-pointer focus:text-burgundy";
    const selectedItemClassName = "bg-burgundy text-white focus:bg-burgundy focus:text-white";
    const unselectedItemClassName = "focus:bg-burgundy/5";

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="inline-flex max-w-full self-start items-center justify-between gap-4 h-9 px-4 mb-2 bg-white rounded-[5px] shadow-[0_2px_6px_rgba(0,0,0,0.15)] text-[11px] uppercase leading-snug tracking-wide text-burgundy outline-none focus-visible:ring-1 focus-visible:ring-burgundy/30"
                >
                    <span className="grid min-w-0 text-left">
                        <span className={cn("col-start-1 row-start-1 truncate", selectedPractitioner && "invisible")}>
                            {t('preferredPractitionerPlaceholder')}
                        </span>
                        {selectedPractitioner && (
                            <span className="col-start-1 row-start-1 truncate">
                                {formatPractitionerName(selectedPractitioner)}
                            </span>
                        )}
                    </span>
                    <ChevronIcon className="w-4 h-4 shrink-0 text-burgundy" strokeWidth={3.25} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                className="min-w-(--radix-dropdown-menu-trigger-width) p-0 bg-white border-0 rounded-[5px] shadow-md"
            >
                <DropdownMenuItem
                    onSelect={handleSelectNoPreference}
                    className={cn(itemClassName, selectedPractitioner ? unselectedItemClassName : selectedItemClassName)}
                >
                    {t('preferredPractitionerNoPreference')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="m-0 bg-burgundy/15" />
                {practitioners.map((practitioner) => {
                    const isSelected = practitioner.id === selectedPractitionerId;
                    return (
                        <DropdownMenuItem
                            key={practitioner.id}
                            onSelect={() => handleSelect(practitioner)}
                            className={cn(itemClassName, isSelected ? selectedItemClassName : unselectedItemClassName)}
                        >
                            {formatPractitionerName(practitioner)}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export const PastPractitionerPickerRow = (props: PastPractitionerPickerProps) => {
    const { t } = useLanguageStore();
    const [infoOpen, setInfoOpen] = useState(false);
    const infoButtonRef = useRef<HTMLButtonElement>(null);
    const [infoWidth, setInfoWidth] = useState(138);

    const updateInfoWidth = () => {
        const button = infoButtonRef.current;
        if (!button) {
            return;
        }
        const iconLeft = button.getBoundingClientRect().left + 5;
        const available = Math.floor(window.innerWidth - iconLeft - 16);
        setInfoWidth(Math.min(138, Math.max(available, 0)));
    };

    useEffect(() => {
        if (!infoOpen) {
            return;
        }
        updateInfoWidth();
        window.addEventListener('resize', updateInfoWidth);
        return () => window.removeEventListener('resize', updateInfoWidth);
    }, [infoOpen]);

    return (
        <div className="flex flex-row items-center gap-0.5">
            <PastPractitionerPicker {...props} />
            <Popover
                open={infoOpen}
                onOpenChange={(open) => {
                    if (open) {
                        updateInfoWidth();
                    }
                    setInfoOpen(open);
                }}
            >
                <PopoverTrigger asChild>
                    <button
                        ref={infoButtonRef}
                        type="button"
                        aria-label={t('preferredPractitionerInfoPast')}
                        className="mb-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-burgundy"
                    >
                        <Info className="h-3.5 w-3.5" />
                    </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" alignOffset={5} sideOffset={2} avoidCollisions={false} style={{ width: infoWidth }} className="max-w-[138px] border-burgundy/15 bg-white p-2.5 text-[11px] font-light leading-snug text-burgundy shadow-md">
                    <p>{t('preferredPractitionerInfoPast')}</p>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default PastPractitionerPicker;
